import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axiosSeoOverview from 'config/axiosSeoOverview';
import './TrafficPerformanceChart.css';

export const TrafficPerformanceChart = ({ urlSearch, seoConfig }) => {
  const { t } = useTranslation();
  const [trafficPerformanceData, setTrafficPerformanceData] = useState(null);
  const [isLoadingTrafficPerformance, setIsLoadingTrafficPerformance] = useState(false);
  const [showPreviousYear, setShowPreviousYear] = useState(true);
  const [isMockData, setIsMockData] = useState(false);

  // tooltip (gardée telle quelle)
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // visibilité (contrôlée via la légende en bas)
  const [visible, setVisible] = useState({
    sessions: true,
    conversions: true,
    clicks: true,
    impressions: true,
  });

  const svgRef = useRef(null);

  // couleurs
  const COLORS = {
    sessions: '#FF855D',
    conversions: '#63D3D7',
    clicks: '#6458F1',
    impressions: '#C06DD8',
  };

  // ---------- Helpers échelle / ticks ----------
  const extent = (arrays) => {
    const vals = arrays.flat().map(Number).filter(Number.isFinite);
    if (!vals.length) return [0, 1];
    return [Math.min(...vals), Math.max(...vals)];
  };

  const padDomain = (min, max, pad = 0.08) => {
    if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];
    if (max === min) {
      const d = Math.max(1, Math.abs(max) || 1);
      return [min - d * pad, max + d * pad];
    }
    const r = max - min;
    return [min - r * pad, max + r * pad];
  };

  const niceStep = (range, count) => {
    const raw = range / Math.max(1, count);
    const pow10 = Math.pow(10, Math.floor(Math.log10(raw)));
    const err = raw / pow10;
    if (err >= 10) return 10 * pow10;
    if (err >= 5) return 5 * pow10;
    if (err >= 2) return 2 * pow10;
    return pow10;
  };

  const makeTicksLinear = (min, max, count = 4) => {
    const step = niceStep(max - min, count);
    const start = Math.ceil(min / step) * step;
    const ticks = [];
    for (let t = start; t <= max + 1e-9; t += step) ticks.push(t);
    if (ticks.length < 2) return [min, max];
    return ticks.slice(0, count + 1);
  };

  const formatSI = (n) => {
    if (!Number.isFinite(n)) return '';
    const sign = n < 0 ? '-' : '';
    let v = Math.abs(n);
    const units = ['', 'k', 'M', 'G', 'T', 'P', 'E'];
    let i = 0;
    while (v >= 1000 && i < units.length - 1) { v /= 1000; i++; }
    const num = v % 1 === 0 ? v : v < 10 ? v.toFixed(1) : Math.round(v);
    return `${sign}${num}${units[i]}`;
  };

  // ---------- Mock ----------
  const buildMockData = () => {
    const days = 30;
    const dates = Array.from({ length: days }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (days - 1 - i));
      return d.toISOString().slice(0, 10);
    });
    const sessions = Array.from({ length: days }, () => 11000 + Math.floor(Math.random() * 800));
    const conversions = Array.from({ length: days }, () => 9500 + Math.floor(Math.random() * 500));
    const clicks = Array.from({ length: days }, () => 18000 + Math.floor(Math.random() * 1200));
    const impressions = Array.from({ length: days }, () => 330000 + Math.floor(Math.random() * 12000));
    return {
      numberOfPeriod: 30,
      device:t('all device') + ' ',
      current: {
        dateList: dates,
        sessionsList: sessions,
        conversionList: conversions,
        newUsersList: [],
        clicks: clicks,
        impressions: impressions
      },
      previous: {
        dateList: dates.map(d => {
          const dt = new Date(d);
          dt.setFullYear(dt.getFullYear() - 1);
          return dt.toISOString().slice(0, 10);
        }),
        sessionsList: sessions.map(v => v + Math.floor(Math.random() * 1500 - 750)),
        conversionList: conversions.map(v => v + Math.floor(Math.random() * 900 - 450)),
        newUsersList: [],
        clicks: clicks.map(v => v + Math.floor(Math.random() * 3000 - 1500)),
        impressions: impressions.map(v => v + Math.floor(Math.random() * 30000 - 15000))
      }
    };
  };

  // ---------- Fetch ----------
  useEffect(() => {
    const fetchTrafficPerformance = async () => {
      if (!urlSearch || !seoConfig || seoConfig.length === 0) {
        setTrafficPerformanceData(null);
        return;
      }

      const normalizeUrl = (url) => {
        if (!url) return '';
        return url.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
      };

      const normalizedSearch = normalizeUrl(urlSearch);

      const matchingConfig = seoConfig.find(config => {
        const normalizedWebsiteName = normalizeUrl(config?.websiteName);
        return normalizedWebsiteName === normalizedSearch;
      });

      if (!matchingConfig || !matchingConfig.activation) {
        setTrafficPerformanceData(null);
        return;
      }

      const { account, websiteId } = matchingConfig;

      if (account && websiteId) {
        setIsLoadingTrafficPerformance(true);
        try {
          const gaDataResponse = await axiosSeoOverview.post(
              `/ga/aggregated-traffic-data`,
              null,
              { params: { account, websiteId, numberOfPeriod: 30, device: 'all device' } }
          );

          const scDataResponse = await axiosSeoOverview.post(
              `/sc/aggregated-search-console`,
              null,
              { params: { account, websiteId, numberOfPeriod: 30, device: 'all device' } }
          );

          const gaData = gaDataResponse?.data || null;
          const scData = scDataResponse?.data || null;

          const currentDateList = scData?.current?.dateList || [];
          const previousDateList = scData?.previous?.dateList || [];

          const mergedTrafficData = {
            numberOfPeriod: gaData?.numberOfPeriod || 30,
            device: gaData?.device || 'all device',
            current: {
              dateList: currentDateList,
              sessionsList: gaData?.current?.sessionsList || [],
              conversionList: gaData?.current?.conversionList || [],
              newUsersList: gaData?.current?.newUsersList || [],
              clicks: scData?.current?.clicks || [],
              impressions: scData?.current?.impressions || []
            },
            previous: {
              dateList: previousDateList,
              sessionsList: gaData?.previous?.sessionsList || [],
              conversionList: gaData?.previous?.conversionList || [],
              newUsersList: gaData?.previous?.newUsersList || [],
              clicks: scData?.previous?.clicks || [],
              impressions: scData?.previous?.impressions || []
            }
          };

          const hasSomeData = (
              (mergedTrafficData.current.sessionsList?.some(n => Number(n) > 0)) ||
              (mergedTrafficData.current.conversionList?.some(n => Number(n) > 0)) ||
              (mergedTrafficData.current.clicks?.some(n => Number(n) > 0)) ||
              (mergedTrafficData.current.impressions?.some(n => Number(n) > 0))
          );

          if (!hasSomeData) {
            setIsMockData(true);
            setTrafficPerformanceData(buildMockData());
          } else {
            setIsMockData(false);
            setTrafficPerformanceData(mergedTrafficData);
          }
        } catch (error) {
          setIsMockData(true);
          setTrafficPerformanceData(buildMockData());
        } finally {
          setIsLoadingTrafficPerformance(false);
        }
      }
    };

    fetchTrafficPerformance();
  }, [urlSearch, seoConfig]);

  // ---------- Formatters ----------
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString('fr-FR', { month: 'short' });
    return `${day} ${month}`;
  };

  const formatDateForTooltip = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString('fr-FR', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // ---------- Géométrie ----------
  const CHART_TOP = 30;
  const CHART_BOTTOM = 250;
  const CHART_HEIGHT = CHART_BOTTOM - CHART_TOP;
  const CHART_WIDTH = 1000;
  const SVG_HEIGHT = CHART_BOTTOM + 20;

  // ---------- Mémo données ----------
  const chartData = useMemo(() => {
    if (!trafficPerformanceData?.current) return null;
    const c = trafficPerformanceData.current;
    const p = trafficPerformanceData.previous;
    return {
      dates: c.dateList || [],
      series: {
        sessions: { current: c.sessionsList || [], previous: p.sessionsList || [] },
        conversions: { current: c.conversionList || [], previous: p.conversionList || [] },
        clicks: { current: c.clicks || [], previous: p.clicks || [] },
        impressions: { current: c.impressions || [], previous: p.impressions || [] }
      }
    };
  }, [trafficPerformanceData]);

  // ---------- Hover (garde ta tooltip) ----------
  const handleChartHover = (event, index) => {
    if (!chartData) return;

    const date = chartData.dates[index];
    const { series } = chartData;

    const sessions = series.sessions.current[index] || 0;
    const conversions = series.conversions.current[index] || 0;
    const clicks = series.clicks.current[index] || 0;
    const impressions = series.impressions.current[index] || 0;

    const previousSessions = series.sessions.previous[index] || 0;
    const previousConversions = series.conversions.previous[index] || 0;
    const previousClicks = series.clicks.previous[index] || 0;
    const previousImpressions = series.impressions.previous[index] || 0;

    setTooltipData({
      date: formatDateForTooltip(date),
      current: { sessions, conversions, clicks, impressions },
      previous: { sessions: previousSessions, conversions: previousConversions, clicks: previousClicks, impressions: previousImpressions }
    });

    // positionnement: x par index, y = moyenne des y visibles
    const chartContainer = event.currentTarget.closest('.chart-grid');
    if (!chartContainer) return;

    const rect = chartContainer.getBoundingClientRect();
    const containerWidth = rect.width;
    const containerHeight = rect.height;

    const n = Math.max(1, chartData.dates.length - 1);
    const xRatio = index / n;
    const pointX = xRatio * containerWidth;

    // scales pour placer la tooltip
    const leftSeriesArrays = [];
    if (visible.sessions) { leftSeriesArrays.push(series.sessions.current, series.sessions.previous); }
    if (visible.conversions) { leftSeriesArrays.push(series.conversions.current, series.conversions.previous); }
    if (visible.clicks) { leftSeriesArrays.push(series.clicks.current, series.clicks.previous); }
    const rightSeriesArrays = visible.impressions ? [series.impressions.current, series.impressions.previous] : [];

    const leftFallback = leftSeriesArrays.length ? leftSeriesArrays : [[0, 1]];
    const rightFallback = rightSeriesArrays.length ? rightSeriesArrays : [[0, 1]];
    let [lMin, lMax] = extent(leftFallback);
    let [rMin, rMax] = extent(rightFallback);
    [lMin, lMax] = padDomain(lMin, lMax, 0.08);
    [rMin, rMax] = padDomain(rMin, rMax, 0.08);

    const yLeft = (v) => {
      const range = lMax - lMin || 1;
      return CHART_BOTTOM - ((Number(v) - lMin) / range) * CHART_HEIGHT;
    };
    const yRight = (v) => {
      const range = rMax - rMin || 1;
      return CHART_BOTTOM - ((Number(v) - rMin) / range) * CHART_HEIGHT;
    };

    const yCandidates = [];
    if (visible.sessions) yCandidates.push(yLeft(sessions));
    if (visible.conversions) yCandidates.push(yLeft(conversions));
    if (visible.clicks) yCandidates.push(yLeft(clicks));
    if (visible.impressions) yCandidates.push(yRight(impressions));

    const pointY = yCandidates.length ? (yCandidates.reduce((a, b) => a + b, 0) / yCandidates.length) : (CHART_TOP + CHART_HEIGHT / 2);
    const pointYPixels = (pointY / (SVG_HEIGHT)) * containerHeight;

    const tooltipWidth = 220;
    const tooltipHeight = 160;
    const shouldShowOnRight = pointX < containerWidth / 2;

    let tooltipX = shouldShowOnRight ? pointX + 20 : pointX - tooltipWidth - 20;
    let tooltipY = pointYPixels - tooltipHeight / 2;

    if (tooltipX + tooltipWidth > containerWidth) tooltipX = containerWidth - tooltipWidth - 10;
    if (tooltipX < 0) tooltipX = 10;
    if (tooltipY < 0) tooltipY = 10;
    if (tooltipY + tooltipHeight > containerHeight) tooltipY = containerHeight - tooltipHeight - 10;

    setTooltipPosition({ x: tooltipX, y: tooltipY });
  };

  const handleChartLeave = () => setTooltipData(null);

  return (
      <div className="content-section" id="evolution-performance">
        <div className="traffic-performance-section">
          <div className="section-header">
            <h2 className="section-title">{t('Évolution des performances')}</h2>
            <div className="chart-controls">
              <div className="controls-left">
                {/* TON toggle Y-1 conservé */}
                <div
                  className="toggle-switch"
                  style={{ display: 'flex', alignItems: 'center', gap: 10, marginRight: '30px' }}
                >
                  <span
                    className="toggle-label"
                    style={{ whiteSpace: 'nowrap', fontSize: '14px', color: '#3c3c3c' }}
                  >
                    {t('Y-1')}
                  </span>
                  <input
                    type="checkbox"
                    className="toggle-input"
                    checked={showPreviousYear}
                    onChange={(e) => setShowPreviousYear(e.target.checked)}
                  />
                  <span
                    className="toggle-slider"
                    onClick={() => setShowPreviousYear(!showPreviousYear)}
                    style={{
                      background: showPreviousYear ? '#000000' : '#e0e0e0',
                      cursor: 'pointer',
                      width: 52,
                      height: 24,
                      borderRadius: 24,
                      position: 'relative',
                      display: 'inline-block',
                      transition: 'background 0.3s ease',
                      flexShrink: 0,
                      border: 'none',
                      outline: 'none',
                      boxShadow: 'none',
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        left: showPreviousYear ? 'calc(100% - 22px)' : '2px',
                        width: 20,
                        height: 20,
                        background: '#fff',
                        borderRadius: '50%',
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none',
                        transition: 'left 0.3s ease',
                        flexShrink: 0,
                        zIndex: 1,
                      }}
                    />
                  </span>
                </div>

                {/* Sélecteurs existants */}
                <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                  <img 
                    src="/Images/date-icon.png" 
                    alt="date icon" 
                    style={{ 
                      width: 16, 
                      height: 16, 
                      marginRight: 8,
                      position: 'absolute',
                      left: 12,
                      pointerEvents: 'none',
                      zIndex: 1
                    }} 
                  />
                  <select className="period-dropdown" style={{ paddingLeft: '36px', paddingRight: '30px' }}>
                    <option value="30">{t('30 days')}</option>
                    <option value="7">{t('7 days')}</option>
                    <option value="90">{t('90 days')}</option>
                  </select>
                  <svg 
                    style={{ 
                      position: 'absolute',
                      right: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 12,
                      height: 12,
                      pointerEvents: 'none',
                      zIndex: 1
                    }}
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M2 4L6 8L10 4" 
                      stroke="#3c3c3c" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                  <img 
                    src="/Images/all-device-icon.png" 
                    alt="device icon" 
                    style={{ 
                      width: 16, 
                      height: 16, 
                      marginRight: 8,
                      position: 'absolute',
                      left: 12,
                      pointerEvents: 'none',
                      zIndex: 1
                    }} 
                  />
                  <select className="device-dropdown" style={{ paddingLeft: '36px', paddingRight: '30px' }}>
                    <option value="all">{t('All devices')}</option>
                    <option value="desktop">{t('Desktop')}</option>
                    <option value="mobile">{t('Mobile')}</option>
                  </select>
                  <svg 
                    style={{ 
                      position: 'absolute',
                      right: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 12,
                      height: 12,
                      pointerEvents: 'none',
                      zIndex: 1
                    }}
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M2 4L6 8L10 4" 
                      stroke="#3c3c3c" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="chart-container">
            {isLoadingTrafficPerformance ? (
                <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
                  {t('Chargement...')}
                </div>
            ) : chartData ? (
                (() => {
                  const { dates, series } = chartData;

                  // Groupes par axe (selon visibilité via LÉGENDE, pas de checkbox en haut)
                  const leftSeriesArrays = [];
                  if (visible.sessions) leftSeriesArrays.push(series.sessions.current, series.sessions.previous);
                  if (visible.conversions) leftSeriesArrays.push(series.conversions.current, series.conversions.previous);
                  if (visible.clicks) leftSeriesArrays.push(series.clicks.current, series.clicks.previous);
                  const rightSeriesArrays = visible.impressions ? [series.impressions.current, series.impressions.previous] : [];

                  const leftFallback = leftSeriesArrays.length ? leftSeriesArrays : [[0, 1]];
                  const rightFallback = rightSeriesArrays.length ? rightSeriesArrays : [[0, 1]];

                  let [lMin, lMax] = extent(leftFallback);
                  let [rMin, rMax] = extent(rightFallback);
                  [lMin, lMax] = padDomain(lMin, lMax, 0.08);
                  [rMin, rMax] = padDomain(rMin, rMax, 0.08);

                  const yLeft = (v) => {
                    const range = lMax - lMin || 1;
                    return CHART_BOTTOM - ((Number(v) - lMin) / range) * (CHART_HEIGHT);
                  };
                  const yRight = (v) => {
                    const range = rMax - rMin || 1;
                    return CHART_BOTTOM - ((Number(v) - rMin) / range) * (CHART_HEIGHT);
                  };

                   // Réduire le nombre de ticks pour augmenter l'espacement entre les lignes
                  const leftTicks = makeTicksLinear(lMin, lMax, 4);
                  const rightTicks = makeTicksLinear(rMin, rMax, 4);

                  const safeDiv = (num, den) => (den === 0 ? 0 : num / den);
                  const xPoint = (idx, n) => safeDiv(idx, Math.max(1, n - 1)) * CHART_WIDTH;

                  const polyPoints = (arr, yFn) =>
                      (arr || []).map((v, idx, a) => `${xPoint(idx, a.length)},${yFn(v)}`).join(' ');

                  return (
                      <div className="line-chart">
                        <div className="chart-grid" style={{ position: 'relative', height: CHART_HEIGHT + 20 }}>
                           <div className="bar-grid">
                            {leftTicks.map((tick, idx) => {
                              const y = yLeft(tick);
                              // Convertir la position Y SVG en pourcentage de la hauteur du conteneur
                              const topPercent = ((y / SVG_HEIGHT) * 100).toFixed(2);
                              return (
                                  <div 
                                      key={`hgrid-${idx}`}
                                      className="bar-grid-line" 
                                      style={{ top: `${topPercent}%` }} 
                                  />
                              );
                            })}
                          </div>
                          
                          <svg
                              ref={svgRef}
                              width="100%"
                              height={SVG_HEIGHT}
                              style={{ position: 'absolute', top: 0, left: 0 }}
                              preserveAspectRatio="none"
                              viewBox={`0 0 ${CHART_WIDTH} ${SVG_HEIGHT}`}
                          >

                            {/* Cadre - ligne X en pointillés (même style que les axes horizontaux) */}
                            <line x1="0" y1={CHART_BOTTOM} x2={CHART_WIDTH} y2={CHART_BOTTOM} stroke="#e5e7eb" strokeWidth="2" strokeDasharray="6 6" />

                            {/* Sessions */}
                            {visible.sessions && (
                                <>
                                  <polyline
                                      points={polyPoints(series.sessions.current, yLeft)}
                                      fill="none"
                                      stroke={COLORS.sessions}
                                      strokeWidth="2"
                                      vectorEffect="non-scaling-stroke"
                                  />
                                  {series.sessions.current.map((v, i, a) => (
                                      <circle key={`pt-s-${i}`} cx={xPoint(i, a.length)} cy={yLeft(v)} r="2" fill={COLORS.sessions} />
                                  ))}
                                  {showPreviousYear && (
                                      <polyline
                                          points={polyPoints(series.sessions.previous, yLeft)}
                                          fill="none"
                                          stroke={COLORS.sessions}
                                          strokeWidth="2"
                                          strokeDasharray="5,5"
                                          opacity="0.5"
                                          vectorEffect="non-scaling-stroke"
                                      />
                                  )}
                                </>
                            )}

                            {/* Conversions */}
                            {visible.conversions && (
                                <>
                                  <polyline
                                      points={polyPoints(series.conversions.current, yLeft)}
                                      fill="none"
                                      stroke={COLORS.conversions}
                                      strokeWidth="2"
                                      vectorEffect="non-scaling-stroke"
                                  />
                                  {series.conversions.current.map((v, i, a) => (
                                      <circle key={`pt-cv-${i}`} cx={xPoint(i, a.length)} cy={yLeft(v)} r="2" fill={COLORS.conversions} />
                                  ))}
                                  {showPreviousYear && (
                                      <polyline
                                          points={polyPoints(series.conversions.previous, yLeft)}
                                          fill="none"
                                          stroke={COLORS.conversions}
                                          strokeWidth="2"
                                          strokeDasharray="5,5"
                                          opacity="0.5"
                                          vectorEffect="non-scaling-stroke"
                                      />
                                  )}
                                </>
                            )}

                            {/* Clicks */}
                            {visible.clicks && (
                                <>
                                  <polyline
                                      points={polyPoints(series.clicks.current, yLeft)}
                                      fill="none"
                                      stroke={COLORS.clicks}
                                      strokeWidth="2"
                                      vectorEffect="non-scaling-stroke"
                                  />
                                  {series.clicks.current.map((v, i, a) => (
                                      <circle key={`pt-cl-${i}`} cx={xPoint(i, a.length)} cy={yLeft(v)} r="2" fill={COLORS.clicks} />
                                  ))}
                                  {showPreviousYear && (
                                      <polyline
                                          points={polyPoints(series.clicks.previous, yLeft)}
                                          fill="none"
                                          stroke={COLORS.clicks}
                                          strokeWidth="2"
                                          strokeDasharray="5,5"
                                          opacity="0.5"
                                          vectorEffect="non-scaling-stroke"
                                      />
                                  )}
                                </>
                            )}

                            {/* Impressions (axe droit) */}
                            {visible.impressions && (
                                <>
                                  <polyline
                                      points={polyPoints(series.impressions.current, yRight)}
                                      fill="none"
                                      stroke={COLORS.impressions}
                                      strokeWidth="2"
                                      vectorEffect="non-scaling-stroke"
                                  />
                                  {series.impressions.current.map((v, i, a) => (
                                      <circle key={`pt-imp-${i}`} cx={xPoint(i, a.length)} cy={yRight(v)} r="2" fill={COLORS.impressions} />
                                  ))}
                                  {showPreviousYear && (
                                      <polyline
                                          points={polyPoints(series.impressions.previous, yRight)}
                                          fill="none"
                                          stroke={COLORS.impressions}
                                          strokeWidth="2"
                                          strokeDasharray="5,5"
                                          opacity="0.5"
                                          vectorEffect="non-scaling-stroke"
                                      />
                                  )}
                                </>
                            )}

                            {/* zones transparentes pour hover (tooltip) */}
                            {dates.map((_, idx) => {
                              const x = xPoint(idx, dates.length);
                              const zoneWidth = CHART_WIDTH / Math.max(1, dates.length - 1);
                              return (
                                  <rect
                                      key={`hover-zone-${idx}`}
                                      x={x - zoneWidth / 2}
                                      y={CHART_TOP}
                                      width={zoneWidth}
                                      height={CHART_HEIGHT}
                                      fill="transparent"
                                      style={{ cursor: 'pointer' }}
                                      onMouseEnter={(e) => handleChartHover(e, idx)}
                                      onMouseLeave={handleChartLeave}
                                  />
                              );
                            })}
                          </svg>

                          {/* Axes Y labels */}
                          <div className="y-axis-values left" style={{ position: 'absolute', right: '100%', marginRight: 10, top: 0, height: SVG_HEIGHT }}>
                            {leftTicks.map((tick, idx) => (
                                <span
                                    key={`yl-${idx}`}
                                    className="y-tick"
                                    style={{ position: 'absolute', top: yLeft(tick), right: 0, width: 56, textAlign: 'right', fontSize: 11, transform: 'translateY(-50%)' }}
                                >
                          {formatSI(tick)}
                        </span>
                            ))}
                          </div>
                            <div className="y-axis-values right" style={{ position: 'absolute', left: '100%', marginLeft: 10, top: 0, height: SVG_HEIGHT }}>
                            {rightTicks.map((tick, idx) => (
                                <span
                                    key={`yr-${idx}`}
                                    className="y-tick"
                                    style={{ position: 'absolute', top: yRight(tick), left: 0, width: 56, textAlign: 'left', fontSize: 11, transform: 'translateY(-50%)' }}
                                >
                          {formatSI(tick)}
                        </span>
                            ))}
                          </div>

                          <div className="y-axis-label-left">{t('Sessions / Conv. / Clicks')}</div>
                          <div className="y-axis-label-right">{t('Impressions')}</div>

                          {/* Tooltip (conservée) */}
                          {tooltipData && (
                              <div
                                  className="chart-tooltip"
                                  style={{
                                    position: 'absolute',
                                    left: `${tooltipPosition.x}px`,
                                    top: `${tooltipPosition.y}px`,
                                    background: 'white',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    zIndex: 1000,
                                    minWidth: '200px',
                                    fontSize: '12px'
                                  }}
                              >
                                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                                  {tooltipData.date}
                                </div>

                                {/* sessions */}
                                <div style={{ marginBottom: '6px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                    <div style={{ width: '8px', height: '8px', background: COLORS.sessions, borderRadius: '50%', marginRight: '6px' }}></div>
                                    <span style={{ marginRight: '8px' }}>Sessions:</span>
                                    <span style={{ fontWeight: 'bold' }}>{(tooltipData.current.sessions || 0).toLocaleString()}</span>
                                  </div>
                                  {showPreviousYear && (
                                      <div style={{ display: 'flex', alignItems: 'center', marginLeft: '14px', marginBottom: '4px' }}>
                                        <div style={{ width: '8px', height: '8px', background: COLORS.sessions, borderRadius: '50%', marginRight: '6px', opacity: 0.5 }}></div>
                                        <span style={{ marginRight: '8px' }}>Y-1:</span>
                                        <span>{(tooltipData.previous.sessions || 0).toLocaleString()}</span>
                                      </div>
                                  )}
                                </div>

                                {/* conversions */}
                                <div style={{ marginBottom: '6px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                    <div style={{ width: '8px', height: '8px', background: COLORS.conversions, borderRadius: '50%', marginRight: '6px' }}></div>
                                    <span style={{ marginRight: '8px' }}>Conversions:</span>
                                    <span style={{ fontWeight: 'bold' }}>{(tooltipData.current.conversions || 0).toLocaleString()}</span>
                                  </div>
                                  {showPreviousYear && (
                                      <div style={{ display: 'flex', alignItems: 'center', marginLeft: '14px', marginBottom: '4px' }}>
                                        <div style={{ width: '8px', height: '8px', background: COLORS.conversions, borderRadius: '50%', marginRight: '6px', opacity: 0.5 }}></div>
                                        <span style={{ marginRight: '8px' }}>Y-1:</span>
                                        <span>{(tooltipData.previous.conversions || 0).toLocaleString()}</span>
                                      </div>
                                  )}
                                </div>

                                {/* clicks */}
                                <div style={{ marginBottom: '6px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                    <div style={{ width: '8px', height: '8px', background: COLORS.clicks, borderRadius: '50%', marginRight: '6px' }}></div>
                                    <span style={{ marginRight: '8px' }}>Clicks:</span>
                                    <span style={{ fontWeight: 'bold' }}>{(tooltipData.current.clicks || 0).toLocaleString()}</span>
                                  </div>
                                  {showPreviousYear && (
                                      <div style={{ display: 'flex', alignItems: 'center', marginLeft: '14px', marginBottom: '4px' }}>
                                        <div style={{ width: '8px', height: '8px', background: COLORS.clicks, borderRadius: '50%', marginRight: '6px', opacity: 0.5 }}></div>
                                        <span style={{ marginRight: '8px' }}>Y-1:</span>
                                        <span>{(tooltipData.previous.clicks || 0).toLocaleString()}</span>
                                      </div>
                                  )}
                                </div>

                                {/* impressions */}
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                    <div style={{ width: '8px', height: '8px', background: COLORS.impressions, borderRadius: '50%', marginRight: '6px' }}></div>
                                    <span style={{ marginRight: '8px' }}>Impressions:</span>
                                    <span style={{ fontWeight: 'bold' }}>{(tooltipData.current.impressions || 0).toLocaleString()}</span>
                                  </div>
                                  {showPreviousYear && (
                                      <div style={{ display: 'flex', alignItems: 'center', marginLeft: '14px' }}>
                                        <div style={{ width: '8px', height: '8px', background: COLORS.impressions, borderRadius: '50%', marginRight: '6px', opacity: 0.5 }}></div>
                                        <span style={{ marginRight: '8px' }}>Y-1:</span>
                                        <span>{(tooltipData.previous.impressions || 0).toLocaleString()}</span>
                                      </div>
                                  )}
                                </div>
                              </div>
                          )}
                        </div>

                        <div className="x-axis" style={{ marginTop: '25px', visibility: 'hidden' }}>
                          {dates.map((date, idx) => (
                              <span key={idx}>{formatDate(date)}</span>
                          ))}
                        </div>
                      </div>
                  );
                })()
            ) : (
                // fallback vide
                (() => {
                  const days = 30;
                  const dates = Array.from({ length: days }, (_, i) => {
                    const d = new Date();
                    d.setDate(d.getDate() - (days - 1 - i));
                    return d.toISOString().slice(0, 10);
                  });

                  return (
                      <div className="line-chart chart-empty-wrapper">
                        <div className="chart-grid blur-target" style={{ position: 'relative', height: SVG_HEIGHT }}>                   
                          <div className="bar-grid">
                     
                            {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
                              const y = CHART_TOP + ratio * (CHART_BOTTOM - CHART_TOP);
                              const topPercent = ((y / SVG_HEIGHT) * 100).toFixed(2);
                              return (
                                <div
                                  key={`empty-h-${idx}`}
                                  className="bar-grid-line"
                                  style={{ top: `${topPercent}%` }}
                                />
                              );
                            })}
                          </div>
                          
                          <svg
                              width="100%"
                              height={SVG_HEIGHT}
                              style={{ position: 'absolute', top: 0, left: 0 }}
                              preserveAspectRatio="none"
                              viewBox={`0 0 ${CHART_WIDTH} ${SVG_HEIGHT}`}
                          >
                            <line x1="0" y1={CHART_BOTTOM} x2={CHART_WIDTH} y2={CHART_BOTTOM} stroke="#e5e7eb" strokeWidth="2" strokeDasharray="6 6" />
                            {/* lignes indicatives */}
                            <line x1="0" y1={CHART_TOP + (1 - 0.55) * (CHART_BOTTOM - CHART_TOP)} x2={CHART_WIDTH} y2={CHART_TOP + (1 - 0.55) * (CHART_BOTTOM - CHART_TOP)} stroke="#69CCF7" strokeWidth="2" />
                            <line x1="0" y1={CHART_TOP + (1 - 0.45) * (CHART_BOTTOM - CHART_TOP)} x2={CHART_WIDTH} y2={CHART_TOP + (1 - 0.45) * (CHART_BOTTOM - CHART_TOP)} stroke="#A05195" strokeWidth="2" />
                          </svg>
                        </div>

                        <div className="x-axis blur-target" style={{ visibility: 'hidden' }}>
                          {dates.map((d, idx) => (
                              <span key={idx}>{formatDate(d)}</span>
                          ))}
                        </div>

                        <div className="empty-overlay">
                          <div className="overlay-card">
                            <span>{t('Pour activer cette fonctionnalité, ajoutez notre adresse ')}</span>
                            <strong style={{ color: '#000' }}>typix@gmail.com</strong>
                            <span>{t(' à votre ')}</span>
                            <strong style={{ color: '#000', fontWeight: 700 }}>{t('Search Console')}</strong>
                            <span>, </span>
                            <a href="#" onClick={(e) => e.preventDefault()} style={{ color: '#0064d2', textDecoration: 'underline' }}>{t('puis cliquez ici pour lancer l’analyse.')}</a>
                          </div>
                        </div>
                      </div>
                  );
                })()
            )}
          </div>

          {/* LÉGENDE SEULE pour (dé)masquer les métriques */}
          <div className="chart-legend">
            <div className="legend-items">
              {[
                ['sessions', t('Sessions')],
                ['conversions', t('Conversions')],
                ['clicks', t('Clicks')],
                ['impressions', t('Impressions')],
              ].map(([key, label]) => (
                  <div
                      key={key}
                      className={`legend-item ${visible[key] ? 'active' : ''}`}
                      onClick={() => setVisible(v => ({ ...v, [key]: !v[key] }))}
                      style={{ cursor: 'pointer' }}
                  >
                    <div className="legend-color" style={{ background: COLORS[key], opacity: visible[key] ? 1 : 0.3 }}></div>
                    <span>{label}</span>
                  </div>
              ))}
            </div>
          </div>
        </div>
      </div>
  );
};
